import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CalendarDays,
  CalendarHeart,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Compass,
  Download,
  FileHeart,
  Heart,
  HouseHeart,
  IdCard,
  Languages,
  LogIn,
  MapPin,
  MapPinned,
  Menu,
  MessagesSquare,
  Moon,
  PawPrint,
  Star,
  Settings,
  Stethoscope,
  Sun,
  UserRoundCheck,
  Users,
  UsersRound,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Link,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { InformationPageContent } from "./components/InformationPageContent";
import {
  fallbackInformationPages,
  informationPageFlags,
  type InformationPageKey,
} from "./content/information-pages";
import {
  getMarketingCopy,
  resolveLocale,
  type FeatureCopy,
  type Locale,
  type MarketingCopy,
} from "./content/marketing";
import { Reveal } from "./motion";

const APP_LINK = "https://get.petcollab.com/";
const BUSINESS_LINK = "https://petcollab.com/app/add-edit-service-company";
const HERO_PHOTO_URL =
  "https://unsplash.com/photos/woman-reads-with-her-dog-in-a-cozy-room-pkDnCljcC8Q";
const PROFESSIONAL_PHOTO_URL = "https://unsplash.com/@neakasa";
const DEFAULT_AUTH_DOMAIN = "auth.petcollab.com";

function resolveAuthLink() {
  const configuredDomain = import.meta.env.VITE_AUTH_DOMAIN?.trim();
  const candidate = configuredDomain || DEFAULT_AUTH_DOMAIN;

  try {
    const url = new URL(
      candidate.includes("://") ? candidate : `https://${candidate}`,
    );

    return url.protocol === "http:" || url.protocol === "https:"
      ? url.origin
      : `https://${DEFAULT_AUTH_DOMAIN}`;
  } catch {
    return `https://${DEFAULT_AUTH_DOMAIN}`;
  }
}

const AUTH_LINK = resolveAuthLink();

type ThemePreference = "system" | "light" | "dark";
type ResolvedTheme = Exclude<ThemePreference, "system">;

interface AppProps {
  remoteContentEnabled: boolean;
}

interface SiteContext {
  copy: MarketingCopy;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  resolvedTheme: ResolvedTheme;
  remoteContentEnabled: boolean;
}

interface IconFeature {
  copy: FeatureCopy;
  icon: LucideIcon;
}

const infoMetadata: Record<
  InformationPageKey,
  Record<Locale, { title: string; description: string }>
> = {
  about: {
    en: {
      title: "About PetCollab | Better pet care starts together",
      description:
        "Learn how PetCollab brings pet people together for connection, shared care, and local discovery.",
    },
    es: {
      title: "Acerca de PetCollab | El cuidado mejora en compañía",
      description:
        "Conoce cómo PetCollab reúne a las personas para conectar, compartir cuidados y descubrir opciones cercanas.",
    },
  },
  contact: {
    en: {
      title: "Contact PetCollab",
      description: "Find PetCollab support and contact information.",
    },
    es: {
      title: "Contacta a PetCollab",
      description:
        "Encuentra información de soporte y contacto de PetCollab.",
    },
  },
  privacy: {
    en: {
      title: "Privacy Policy | PetCollab",
      description: "Read the PetCollab privacy policy.",
    },
    es: {
      title: "Política de privacidad | PetCollab",
      description: "Lee la política de privacidad de PetCollab.",
    },
  },
  terms: {
    en: {
      title: "Terms of Use | PetCollab",
      description: "Read the PetCollab terms of use.",
    },
    es: {
      title: "Términos de uso | PetCollab",
      description: "Lee los términos de uso de PetCollab.",
    },
  },
  cookies: {
    en: {
      title: "Cookie Policy | PetCollab",
      description: "Learn how the PetCollab website uses browser storage.",
    },
    es: {
      title: "Política de cookies | PetCollab",
      description:
        "Conoce cómo el sitio de PetCollab usa el almacenamiento del navegador.",
    },
  },
  business: {
    en: {
      title: "Registering your pet business | PetCollab",
      description:
        "Learn how pet professionals can create and manage a PetCollab business presence.",
    },
    es: {
      title: "Registra tu negocio de mascotas | PetCollab",
      description:
        "Conoce cómo los profesionales pueden crear y administrar su presencia en PetCollab.",
    },
  },
};

function getInitialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem("petcollab-locale");

    if (stored === "en" || stored === "es") {
      return stored;
    }
  } catch {
    // Browser storage can be blocked; the device language remains available.
  }

  return resolveLocale(window.navigator.language);
}

function getInitialThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem("petcollab-theme");

    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
}

function useSiteState(remoteContentEnabled: boolean): SiteContext {
  const [locale, setLocale] = useState<Locale>(getInitialLocale);
  const [themePreference, setThemePreference] =
    useState<ThemePreference>(getInitialThemePreference);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() =>
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const updateSystemTheme = () => {
      setSystemTheme(mediaQuery.matches ? "dark" : "light");
    };

    updateSystemTheme();
    mediaQuery.addEventListener("change", updateSystemTheme);

    return () => mediaQuery.removeEventListener("change", updateSystemTheme);
  }, []);

  const resolvedTheme =
    themePreference === "system" ? systemTheme : themePreference;

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;

    const themeColor = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    themeColor?.setAttribute(
      "content",
      resolvedTheme === "dark" ? "#211B17" : "#FBF7EF",
    );

    try {
      if (themePreference === "system") {
        window.localStorage.removeItem("petcollab-theme");
      } else {
        window.localStorage.setItem("petcollab-theme", themePreference);
      }
    } catch {
      // The active theme still applies when persistence is unavailable.
    }
  }, [resolvedTheme, themePreference]);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      window.localStorage.setItem("petcollab-locale", locale);
    } catch {
      // The selected locale still applies for the current visit.
    }
    upsertMeta(
      'meta[property="og:locale"]',
      "content",
      locale === "es" ? "es_ES" : "en_US",
    );
  }, [locale]);

  const copy = useMemo(() => getMarketingCopy(locale), [locale]);

  return {
    copy,
    locale,
    remoteContentEnabled,
    resolvedTheme,
    setLocale,
    setThemePreference,
    themePreference,
  };
}

function upsertMeta(selector: string, attribute: string, value: string) {
  document.querySelector<HTMLMetaElement>(selector)?.setAttribute(attribute, value);
}

function usePageMetadata(title: string, description: string) {
  const location = useLocation();

  useEffect(() => {
    document.title = title;
    upsertMeta('meta[name="description"]', "content", description);
    upsertMeta('meta[property="og:title"]', "content", title);
    upsertMeta('meta[property="og:description"]', "content", description);
    upsertMeta('meta[name="twitter:title"]', "content", title);
    upsertMeta('meta[name="twitter:description"]', "content", description);

    const canonicalUrl = new URL(location.pathname, window.location.origin).href;
    upsertMeta('meta[property="og:url"]', "content", canonicalUrl);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.append(canonical);
    }

    canonical.href = canonicalUrl;
  }, [description, location.pathname, title]);
}

function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      window.requestAnimationFrame(() => {
        document
          .getElementById(location.hash.slice(1))
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }

    window.scrollTo({ left: 0, top: 0 });
  }, [location.hash, location.pathname]);

  return null;
}

function ExternalIconLabel({ label }: { label: string }) {
  return <span className="sr-only"> — {label}</span>;
}

function AppLink({
  children,
  className = "button button--primary",
  externalLabel,
}: {
  children: ReactNode;
  className?: string;
  externalLabel: string;
}) {
  return (
    <a className={className} href={APP_LINK} rel="noreferrer" target="_blank">
      {children}
      <ExternalIconLabel label={externalLabel} />
    </a>
  );
}

function Brand({
  copy,
  variant = "wordmark",
}: {
  copy: MarketingCopy;
  variant?: "wordmark" | "full";
}) {
  const assetName = variant === "full" ? "petcollab-full" : "petcollab-wordmark";
  const assetClass = variant === "full" ? "fullmark" : "wordmark";

  return (
    <Link
      aria-label={copy.accessibility.homeLink}
      className={`brand brand--${variant}`}
      to="/"
    >
      <img
        aria-hidden="true"
        className={`brand-asset brand-asset--light ${assetClass}`}
        src={`/brand/${assetName}-light.svg`}
      />
      <img
        aria-hidden="true"
        className={`brand-asset brand-asset--dark ${assetClass}`}
        src={`/brand/${assetName}-dark.svg`}
      />
      <span className="sr-only">{copy.brand.name}</span>
    </Link>
  );
}

function Header({ site }: { site: SiteContext }) {
  const { copy } = site;
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileMenuOpen]);

  const navigation = [
    { label: copy.nav.community, to: "/#community" },
    { label: copy.nav.petCare, to: "/#pet-care" },
    { label: copy.nav.findCare, to: "/#find-care" },
    { label: copy.nav.discover, to: "/#discover" },
  ];
  const navLinks = (mobile = false) => (
    <>
      {navigation.map((item) => (
        <Link className="nav-link" key={item.to} to={item.to}>
          {item.label}
        </Link>
      ))}
      <Link
        aria-current={location.pathname === "/business" ? "page" : undefined}
        className="nav-link nav-link--business"
        to="/business"
      >
        {copy.nav.business}
        {mobile ? <ChevronRight aria-hidden="true" size={18} /> : null}
      </Link>
      {mobile ? (
        <a className="nav-link nav-link--sign-in" href={AUTH_LINK}>
          <LogIn aria-hidden="true" size={18} />
          {copy.controls.signIn}
          <ChevronRight aria-hidden="true" size={18} />
        </a>
      ) : null}
    </>
  );

  return (
    <header className="site-header">
      <div className="header-inner">
        <Brand copy={copy} />

        <nav aria-label={copy.accessibility.primaryNavigation} className="desktop-nav">
          {navLinks()}
        </nav>

        <div className="header-actions">
          <a className="button sign-in-button sign-in-button--header" href={AUTH_LINK}>
            <LogIn aria-hidden="true" size={18} />
            {copy.controls.signIn}
          </a>

          <button
            aria-expanded={mobileMenuOpen}
            aria-label={
              mobileMenuOpen
                ? copy.accessibility.closeMenu
                : copy.accessibility.openMenu
            }
            aria-controls="mobile-navigation"
            className="icon-button mobile-menu-button"
            onClick={() => setMobileMenuOpen((open) => !open)}
            type="button"
          >
            {mobileMenuOpen ? (
              <X aria-hidden="true" size={22} />
            ) : (
              <Menu aria-hidden="true" size={22} />
            )}
          </button>
        </div>
      </div>

      <nav
        aria-label={copy.accessibility.primaryNavigation}
        className="mobile-nav"
        hidden={!mobileMenuOpen}
        id="mobile-navigation"
      >
        {navLinks(true)}
      </nav>
    </header>
  );
}

function PhotoCredit({
  author,
  href,
  locale,
}: {
  author: string;
  href: string;
  locale: Locale;
}) {
  return (
    <small className="photo-credit">
      {locale === "es" ? "Foto de" : "Photo by"}{" "}
      <a href={href} rel="noreferrer" target="_blank">
        {author}
      </a>{" "}
      {locale === "es" ? "en Unsplash" : "on Unsplash"}
    </small>
  );
}

function SectionHeading({
  eyebrow,
  title,
  body,
  align = "center",
}: {
  eyebrow: string;
  title: string;
  body: string;
  align?: "center" | "left";
}) {
  return (
    <div className={`section-heading section-heading--${align}`}>
      <p className="section-kicker">{eyebrow}</p>
      <h2 className="section-title">{title}</h2>
      <p className="section-copy">{body}</p>
    </div>
  );
}

function FeatureCard({
  feature,
  modifier,
  order = 0,
}: {
  feature: IconFeature;
  modifier?: string;
  order?: number;
}) {
  const Icon = feature.icon;

  return (
    <Reveal
      as="article"
      className={`feature-card motion-lift${modifier ? ` ${modifier}` : ""}`}
      order={order}
      stagger={75}
      variant="up"
    >
      <span className="icon-wrap">
        <Icon aria-hidden="true" size={24} strokeWidth={1.8} />
      </span>
      <h3>{feature.copy.title}</h3>
      <p>{feature.copy.body}</p>
    </Reveal>
  );
}

function PetProfilePreview({ locale }: { locale: Locale }) {
  const labels =
    locale === "es"
      ? {
          descriptor: "Mezcla de golden · 4 años",
          circle: "Círculo de cuidados",
          people: "3 personas",
          record: "Vacunas",
          status: "Al día",
          note: "Ama los paseos tranquilos y sus juguetes que crujen.",
        }
      : {
          descriptor: "Golden mix · 4 years",
          circle: "Care circle",
          people: "3 people",
          record: "Vaccinations",
          status: "Up to date",
          note: "Loves slow walks and every toy that crinkles.",
        };

  return (
    <div aria-hidden="true" className="pet-profile-card">
      <div className="pet-avatar">
        <PawPrint size={28} strokeWidth={1.8} />
      </div>
      <div className="pet-profile-heading">
        <p className="pet-name">Milo</p>
        <p>{labels.descriptor}</p>
      </div>
      <p className="pet-note">“{labels.note}”</p>
      <div className="pet-detail">
        <UsersRound size={20} />
        <span>
          <strong>{labels.circle}</strong>
          {labels.people}
        </span>
      </div>
      <div className="pet-detail">
        <FileHeart size={20} />
        <span>
          <strong>{labels.record}</strong>
          {labels.status}
        </span>
        <CheckCircle2 className="pet-detail-check" size={19} />
      </div>
    </div>
  );
}

function Hero({ site }: { site: SiteContext }) {
  const { copy, locale } = site;
  const hero = copy.home.hero;
  const details =
    locale === "es"
      ? {
          circleTitle: "El círculo de tu mascota",
          circleBody: "Personas, planes y cuidados, juntos",
          careChip: "Cuidados compartidos",
          benefits: [
            {
              title: "Comunidad primero",
              body: "Conoce personas con mascotas cerca de ti",
              icon: UsersRound,
            },
            {
              title: "Cuidados coordinados",
              body: "Mantén a las personas correctas al día",
              icon: UserRoundCheck,
            },
            {
              title: "Descubre cerca de ti",
              body: "Encuentra ayuda cuando la necesites",
              icon: MapPinned,
            },
          ],
        }
      : {
          circleTitle: "Your pet’s circle",
          circleBody: "People, plans, and care—together",
          careChip: "Shared care, in sync",
          benefits: [
            {
              title: "Community first",
              body: "Meet pet people nearby",
              icon: UsersRound,
            },
            {
              title: "Care together",
              body: "Keep the right people in sync",
              icon: UserRoundCheck,
            },
            {
              title: "Discover locally",
              body: "Find support when you need it",
              icon: MapPinned,
            },
          ],
        };

  return (
    <section className="hero">
      <div className="hero-layout">
        <Reveal
          className="hero-copy-block hero-inner"
          distance={28}
          duration={720}
          variant="right"
        >
          <p className="hero-eyebrow">{hero.eyebrow}</p>
          <h1 className="hero-title">{hero.title}</h1>
          <p className="hero-copy">{hero.body}</p>
          <div className="hero-actions">
            <AppLink externalLabel={copy.accessibility.externalLink}>
              <Download aria-hidden="true" size={19} />
              {hero.primaryCta}
            </AppLink>
            <a className="button button--secondary" href="#community">
              {hero.secondaryCta}
              <ArrowRight aria-hidden="true" size={19} />
            </a>
          </div>
        </Reveal>

        <Reveal
          className="hero-media organic-frame"
          delay={120}
          distance={18}
          duration={800}
          variant="scale"
        >
          <img
            alt={hero.imageAlt}
            fetchPriority="high"
            height="1350"
            src="/images/pet-parent-home.jpg"
            width="2400"
          />
          <div className="hero-circle-card">
            <span className="hero-circle-card__icon" aria-hidden="true">
              <UsersRound size={22} strokeWidth={1.8} />
            </span>
            <span className="hero-circle-card__copy">
              <strong>{details.circleTitle}</strong>
              <small>{details.circleBody}</small>
            </span>
            <span className="avatar-stack" aria-hidden="true">
              <i>M</i>
              <i>J</i>
              <i>+3</i>
            </span>
          </div>
          <div className="hero-care-chip">
            <Heart aria-hidden="true" size={17} fill="currentColor" />
            {details.careChip}
          </div>
          <PhotoCredit
            author="Vitaly Gariev"
            href={HERO_PHOTO_URL}
            locale={locale}
          />
        </Reveal>
      </div>

      <div className="hero-benefits" aria-label={hero.eyebrow}>
        {details.benefits.map((benefit, index) => {
          const Icon = benefit.icon;

          return (
            <Reveal
              className="hero-benefit"
              key={benefit.title}
              order={index}
              stagger={90}
            >
              <span aria-hidden="true">
                <Icon size={21} strokeWidth={1.8} />
              </span>
              <p>
                <strong>{benefit.title}</strong>
                <small>{benefit.body}</small>
              </p>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function CommunitySection({ site }: { site: SiteContext }) {
  const section = site.copy.home.community;
  const features: IconFeature[] = [
    { copy: section.features.circles, icon: UsersRound },
    { copy: section.features.petMatch, icon: Heart },
    { copy: section.features.messaging, icon: MessagesSquare },
    { copy: section.features.events, icon: CalendarHeart },
  ];

  return (
    <section className="section section--community" id="community">
      <div className="community-layout">
        <Reveal className="community-intro" variant="right">
          <SectionHeading align="left" {...section} />
          <div className="community-connection" aria-hidden="true">
            <span><PawPrint size={19} /></span>
            <i />
            <span><UsersRound size={19} /></span>
            <i />
            <span><Heart size={18} /></span>
          </div>
        </Reveal>
        <div className="quilt quilt--community">
          {features.map((feature, index) => (
            <FeatureCard
              feature={feature}
              key={feature.copy.title}
              modifier={`quilt-card quilt-card--${index + 1}`}
              order={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function PetCareSection({ site }: { site: SiteContext }) {
  const section = site.copy.home.petCare;
  const features: IconFeature[] = [
    { copy: section.features.profile, icon: PawPrint },
    { copy: section.features.petId, icon: IdCard },
    { copy: section.features.sharedOwnership, icon: UserRoundCheck },
    { copy: section.features.records, icon: FileHeart },
  ];

  return (
    <section className="section section--panel" id="pet-care">
      <div className="story-band">
        <Reveal className="story-copy" variant="right">
          <SectionHeading align="left" {...section} />
          <ul className="story-list">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <li key={feature.copy.title}>
                  <Icon aria-hidden="true" size={21} />
                  <span>
                    <strong>{feature.copy.title}</strong>
                    {feature.copy.body}
                  </span>
                </li>
              );
            })}
          </ul>
        </Reveal>
        <Reveal
          className="story-media story-media--profile"
          delay={100}
          variant="scale"
        >
          <span className="organic-spot organic-spot--sage" />
          <PetProfilePreview locale={site.locale} />
        </Reveal>
      </div>
    </section>
  );
}

function FindCareSection({ site }: { site: SiteContext }) {
  const section = site.copy.home.findCare;
  const features: IconFeature[] = [
    { copy: section.features.places, icon: MapPin },
    { copy: section.features.services, icon: Stethoscope },
    { copy: section.features.reviews, icon: Star },
    { copy: section.features.booking, icon: CalendarCheck },
  ];

  return (
    <section className="section" id="find-care">
      <Reveal>
        <SectionHeading {...section} />
      </Reveal>
      <div className="care-layout">
        <Reveal
          className="story-media story-media--photo organic-frame"
          variant="right"
        >
          <img
            alt={section.imageAlt}
            height="1600"
            loading="lazy"
            src="/images/pet-professional.jpg"
            width="1200"
          />
          <PhotoCredit
            author="Neakasa"
            href={PROFESSIONAL_PHOTO_URL}
            locale={site.locale}
          />
        </Reveal>
        <div className="feature-grid feature-grid--care">
          {features.map((feature, index) => (
            <FeatureCard
              feature={feature}
              key={feature.copy.title}
              order={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function DiscoverSection({ site }: { site: SiteContext }) {
  const section = site.copy.home.discover;
  const features: IconFeature[] = [
    { copy: section.features.adoption, icon: HouseHeart },
    { copy: section.features.events, icon: CalendarDays },
    { copy: section.features.nearby, icon: Compass },
  ];

  return (
    <section className="section section--moss" id="discover">
      <Reveal>
        <SectionHeading {...section} />
      </Reveal>
      <div className="feature-grid feature-grid--three">
        {features.map((feature, index) => (
          <FeatureCard
            feature={feature}
            key={feature.copy.title}
            order={index}
          />
        ))}
      </div>
    </section>
  );
}

function ClosingSections({ site }: { site: SiteContext }) {
  const { copy } = site;
  const download = copy.home.download;
  const business = copy.home.businessTeaser;

  return (
    <div className="closing-sections">
      <Reveal as="section" className="download-banner" variant="scale">
        <span className="download-mark" aria-hidden="true">
          <PawPrint size={42} strokeWidth={1.5} />
        </span>
        <div className="download-banner__copy">
          <p className="section-kicker">{download.eyebrow}</p>
          <h2>{download.title}</h2>
          <p>{download.body}</p>
        </div>
        <AppLink externalLabel={copy.accessibility.externalLink}>
          {download.cta}
          <ArrowRight aria-hidden="true" size={19} />
        </AppLink>
      </Reveal>

      <Reveal as="section" className="business-teaser">
        <div className="business-teaser__icon" aria-hidden="true">
          <BriefcaseBusiness size={34} strokeWidth={1.6} />
        </div>
        <div className="business-teaser__copy">
          <p className="section-kicker">{business.eyebrow}</p>
          <h2>{business.title}</h2>
          <p>{business.body}</p>
        </div>
        <Link className="button button--secondary" to="/business">
          {business.cta}
          <ArrowRight aria-hidden="true" size={19} />
        </Link>
      </Reveal>
    </div>
  );
}

function HomePage({ site }: { site: SiteContext }) {
  usePageMetadata(site.copy.meta.homeTitle, site.copy.meta.homeDescription);

  return (
    <main className="page" id="main-content">
      <Hero site={site} />
      <CommunitySection site={site} />
      <PetCareSection site={site} />
      <FindCareSection site={site} />
      <DiscoverSection site={site} />
      <ClosingSections site={site} />
    </main>
  );
}

function BusinessPage({ site }: { site: SiteContext }) {
  const { business } = site.copy;
  const providerPreview =
    site.locale === "es"
      ? {
          title: "Tu agenda, más clara",
          body: "Servicios, equipo y citas en un solo lugar",
        }
      : {
          title: "A clearer day ahead",
          body: "Services, team, and appointments in one place",
        };
  usePageMetadata(
    site.copy.meta.businessTitle,
    site.copy.meta.businessDescription,
  );

  const capabilityIcons: Record<keyof typeof business.capabilities.features, LucideIcon> = {
    organizations: Building2,
    locations: MapPinned,
    services: Stethoscope,
    team: Users,
    schedules: Clock3,
    appointments: CalendarCheck,
    events: CalendarDays,
    adoptionListings: HouseHeart,
  };
  const capabilities = Object.entries(business.capabilities.features) as Array<
    [keyof typeof business.capabilities.features, FeatureCopy]
  >;
  const steps = Object.values(business.gettingStarted.steps);

  return (
    <main className="page business-page" id="main-content">
      <section className="business-hero">
        <Reveal className="business-hero__copy" variant="right">
          <p className="hero-eyebrow">{business.hero.eyebrow}</p>
          <h1 className="hero-title">{business.hero.title}</h1>
          <p className="hero-copy">{business.hero.body}</p>
          <div className="hero-actions">
            <AppLink externalLabel={site.copy.accessibility.externalLink}>
              {business.hero.primaryCta}
              <ArrowRight aria-hidden="true" size={19} />
            </AppLink>
            <a className="button button--secondary" href="#business-tools">
              {business.hero.secondaryCta}
            </a>
          </div>
        </Reveal>
        <Reveal
          className="business-hero__media organic-frame"
          delay={100}
          variant="scale"
        >
          <img
            alt={business.hero.imageAlt}
            height="1600"
            src="/images/pet-professional.jpg"
            width="1200"
          />
          <PhotoCredit
            author="Neakasa"
            href={PROFESSIONAL_PHOTO_URL}
            locale={site.locale}
          />
          <div className="business-hero__badge">
            <span aria-hidden="true">
              <CalendarCheck size={22} strokeWidth={1.8} />
            </span>
            <p>
              <strong>{providerPreview.title}</strong>
              <small>{providerPreview.body}</small>
            </p>
          </div>
        </Reveal>
      </section>

      <section className="section business-overview">
        <Reveal variant="right">
          <SectionHeading align="left" {...business.overview} />
        </Reveal>
        <Reveal as="aside" className="audience-card motion-lift" delay={90}>
          <UsersRound aria-hidden="true" size={28} />
          <p>{business.overview.audience}</p>
          <Link className="text-link" to="/business/register">
            {site.locale === "es"
              ? "Consulta los detalles de registro"
              : "Read the registration details"}
            <ArrowRight aria-hidden="true" size={18} />
          </Link>
        </Reveal>
      </section>

      <section className="section section--panel" id="business-tools">
        <Reveal>
          <SectionHeading {...business.capabilities} />
        </Reveal>
        <div className="feature-grid feature-grid--business">
          {capabilities.map(([key, feature], index) => (
            <FeatureCard
              feature={{ copy: feature, icon: capabilityIcons[key] }}
              key={key}
              order={index}
            />
          ))}
        </div>
      </section>

      <section className="section">
        <Reveal>
          <SectionHeading {...business.gettingStarted} />
        </Reveal>
        <ol className="steps-grid">
          {steps.map((step, index) => (
            <Reveal
              as="li"
              className="step-card motion-lift"
              key={step.title}
              order={index}
              stagger={90}
            >
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </Reveal>
          ))}
        </ol>
      </section>

      <Reveal as="section" className="download-banner business-cta" variant="scale">
        <div>
          <p className="section-kicker">{business.cta.eyebrow}</p>
          <h2>{business.cta.title}</h2>
          <p>{business.cta.body}</p>
          <small>{business.cta.note}</small>
        </div>
        <a
          className="button button--primary"
          href={BUSINESS_LINK}
          rel="noreferrer"
          target="_blank"
        >
          {business.cta.button}
          <ArrowRight aria-hidden="true" size={19} />
          <ExternalIconLabel label={site.copy.accessibility.externalLink} />
        </a>
      </Reveal>
    </main>
  );
}

function InformationPage({
  pageKey,
  site,
}: {
  pageKey: InformationPageKey;
  site: SiteContext;
}) {
  const metadata = infoMetadata[pageKey][site.locale];
  usePageMetadata(metadata.title, metadata.description);

  return (
    <main className="page info-shell" id="main-content">
      <div className="info-card">
        <InformationPageContent
          className="info-content"
          fallback={fallbackInformationPages[pageKey][site.locale]}
          flagName={informationPageFlags[pageKey].en}
          locale={site.locale}
          remoteEnabled={site.remoteContentEnabled}
        />
      </div>
    </main>
  );
}

function NotFoundPage({ site }: { site: SiteContext }) {
  usePageMetadata(
    site.copy.meta.notFoundTitle,
    site.copy.notFound.body,
  );

  return (
    <main className="page not-found" id="main-content">
      <div className="not-found__mark" aria-hidden="true">
        <PawPrint size={64} strokeWidth={1.4} />
      </div>
      <p className="hero-eyebrow">{site.copy.notFound.eyebrow}</p>
      <h1>{site.copy.notFound.title}</h1>
      <p>{site.copy.notFound.body}</p>
      <div className="hero-actions">
        <Link className="button button--primary" to="/">
          {site.copy.notFound.homeCta}
        </Link>
        <Link className="button button--secondary" to="/business">
          {site.copy.notFound.businessCta}
        </Link>
      </div>
    </main>
  );
}

function Footer({ site }: { site: SiteContext }) {
  const {
    copy,
    locale,
    resolvedTheme,
    setLocale,
    setThemePreference,
    themePreference,
  } = site;
  const ThemeIcon = resolvedTheme === "dark" ? Moon : Sun;
  const yearCopyright = copy.footer.copyright.replace(
    "© PetCollab",
    `© ${new Date().getFullYear()} PetCollab`,
  );

  return (
    <footer className="site-footer">
      <div className="footer-grid">
        <div className="footer-brand">
          <Brand copy={copy} variant="full" />
          <p>{copy.footer.tagline}</p>
          <AppLink
            className="text-link"
            externalLabel={copy.accessibility.externalLink}
          >
            {copy.common.getTheApp}
            <ArrowRight aria-hidden="true" size={17} />
          </AppLink>
        </div>

        <nav aria-label={copy.accessibility.footerNavigation} className="footer-links">
          <div className="footer-links__group">
            <h2>{copy.footer.exploreTitle}</h2>
            <Link to="/#community">{copy.nav.community}</Link>
            <Link to="/#pet-care">{copy.nav.petCare}</Link>
            <Link to="/#find-care">{copy.nav.findCare}</Link>
            <Link to="/#discover">{copy.nav.discover}</Link>
          </div>
          <div className="footer-links__group">
            <h2>{copy.footer.companyTitle}</h2>
            <Link to="/about">{copy.footer.about}</Link>
            <Link to="/contact">{copy.footer.contact}</Link>
            <Link to="/business">{copy.nav.business}</Link>
          </div>
          <div className="footer-links__group">
            <h2>{copy.footer.legalTitle}</h2>
            <Link to="/privacy">{copy.footer.privacy}</Link>
            <Link to="/terms">{copy.footer.terms}</Link>
            <Link to="/cookies">{copy.footer.cookies}</Link>
          </div>
        </nav>

        <aside className="footer-business">
          <BriefcaseBusiness aria-hidden="true" size={24} />
          <p>{copy.footer.businessPrompt}</p>
          <Link className="text-link" to="/business">
            {copy.footer.businessLink}
            <ChevronRight aria-hidden="true" size={17} />
          </Link>
        </aside>

        <div className="footer-settings-shell">
          <details className="footer-settings">
            <summary>
              <Settings aria-hidden="true" size={19} />
              <span>{copy.controls.settings}</span>
              <ChevronRight
                aria-hidden="true"
                className="footer-settings__chevron"
                size={18}
              />
            </summary>
            <div className="footer-settings__panel">
              <div className="footer-setting">
                <span className="footer-setting__label">
                  <Languages aria-hidden="true" size={18} />
                  {copy.controls.language}
                </span>
                <div
                  aria-label={copy.accessibility.languageSwitcher}
                  className="locale-switch footer-locale-switch"
                  role="group"
                >
                  <button
                    aria-pressed={locale === "en"}
                    className="locale-option"
                    onClick={() => setLocale("en")}
                    title={copy.controls.switchToEnglish}
                    type="button"
                  >
                    EN
                  </button>
                  <span aria-hidden="true">/</span>
                  <button
                    aria-pressed={locale === "es"}
                    className="locale-option"
                    onClick={() => setLocale("es")}
                    title={copy.controls.switchToSpanish}
                    type="button"
                  >
                    ES
                  </button>
                </div>
              </div>

              <label className="footer-setting">
                <span className="footer-setting__label">
                  <ThemeIcon aria-hidden="true" size={18} />
                  {copy.controls.theme}
                </span>
                <select
                  aria-label={copy.accessibility.themeSwitcher}
                  className="footer-theme-select"
                  onChange={(event) =>
                    setThemePreference(event.target.value as ThemePreference)
                  }
                  value={themePreference}
                >
                  <option value="system">{copy.controls.system}</option>
                  <option value="light">{copy.controls.light}</option>
                  <option value="dark">{copy.controls.dark}</option>
                </select>
              </label>
            </div>
          </details>
        </div>
      </div>
      <div className="footer-bottom">
        <p>{yearCopyright}</p>
        <a href="#top">{copy.accessibility.scrollToTop} ↑</a>
      </div>
    </footer>
  );
}

export function App({ remoteContentEnabled }: AppProps) {
  const site = useSiteState(remoteContentEnabled);
  const location = useLocation();

  return (
    <div className="app-shell" id="top">
      <a className="skip-link" href="#main-content">
        {site.copy.accessibility.skipToContent}
      </a>
      <ScrollManager />
      <Header key={`${location.pathname}${location.hash}`} site={site} />
      <Routes>
        <Route element={<HomePage site={site} />} path="/" />
        <Route element={<BusinessPage site={site} />} path="/business" />
        <Route
          element={<InformationPage pageKey="business" site={site} />}
          path="/business/register"
        />
        <Route
          element={<InformationPage pageKey="about" site={site} />}
          path="/about"
        />
        <Route
          element={<InformationPage pageKey="contact" site={site} />}
          path="/contact"
        />
        <Route
          element={<InformationPage pageKey="privacy" site={site} />}
          path="/privacy"
        />
        <Route
          element={<InformationPage pageKey="terms" site={site} />}
          path="/terms"
        />
        <Route
          element={<InformationPage pageKey="cookies" site={site} />}
          path="/cookies"
        />
        <Route element={<NotFoundPage site={site} />} path="*" />
      </Routes>
      <Footer site={site} />
    </div>
  );
}
